import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, FileUp, Loader2, Presentation, Share2, X } from "lucide-react";
import { api, img } from "../lib/api";

type Preset = { id: string; platform: string; label: string; w: number; h: number };
type Options = {
  presets: Preset[];
  defaults: string[];
  template: { name: string; filename: string; layouts: number; imageLayouts: { cover: string; single: string[]; full: string[]; pair: string[] } } | null;
  blocked: { id: string; title: string; reason: string }[];
};
type Thumb = { id: string; version?: number; title?: string };

/** Hämtar en fil via POST och sparar den med namnet servern föreslår. */
async function download(path: string, body: unknown) {
  const res = await fetch(`/api/${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? "Exporten misslyckades");
  const name = res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "export";
  const skipped = JSON.parse(decodeURIComponent(res.headers.get("x-bildbanken-skipped") ?? "%5B%5D")) as unknown[];
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { name, skipped: skipped.length };
}

export function ExportDialog({ items, initial = "social", onClose }: { items: Thumb[]; initial?: "social" | "pptx"; onClose: () => void }) {
  const [tab, setTab] = useState(initial);
  const [opts, setOpts] = useState<Options | null>(null);
  const ids = items.map((i) => i.id);
  const reload = () => api<Options>(`export/options?ids=${ids.join(",")}`).then(setOpts);
  useEffect(() => { reload(); }, [ids.join(",")]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const blockedIds = new Set(opts?.blocked.map((b) => b.id));
  const usable = items.filter((i) => !blockedIds.has(i.id));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4 backdrop-blur-sm" onMouseDown={onClose}>
      <div className="card flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-line px-5 py-3">
          <div className="flex-1 font-semibold">Exportera {items.length} bild{items.length === 1 ? "" : "er"}</div>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Stäng"><X size={18} /></button>
        </div>
        <div className="flex gap-1 border-b border-line px-5 pt-2">
          {([["social", "Sociala medier", Share2], ["pptx", "PowerPoint", Presentation]] as const).map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 font-medium ${tab === k ? "border-ember text-ink" : "border-transparent text-soft hover:text-ink"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto p-5">
          {opts && opts.blocked.length > 0 && (
            <div className="mb-4 flex gap-2 rounded-lg bg-warn-soft p-3 text-[13px] text-warn">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <b>{opts.blocked.length} bild{opts.blocked.length === 1 ? "" : "er"} följer inte med:</b>
                <ul className="mt-0.5">{opts.blocked.map((b) => <li key={b.id}>{b.title} – {b.reason}</li>)}</ul>
              </div>
            </div>
          )}
          {!opts ? <Loader2 className="animate-spin text-muted" /> : tab === "social"
            ? <Social opts={opts} items={usable} onDone={onClose} />
            : <Deck opts={opts} items={usable} onTemplate={reload} onDone={onClose} />}
        </div>
      </div>
    </div>
  );
}

// ---------- Sociala medier ----------
function Social({ opts, items, onDone }: { opts: Options; items: Thumb[]; onDone: () => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set(opts.defaults));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const first = items[0];
  const byPlatform = useMemo(() => {
    const m = new Map<string, Preset[]>();
    for (const p of opts.presets) (m.get(p.platform) ?? m.set(p.platform, []).get(p.platform)!).push(p);
    return [...m];
  }, [opts.presets]);
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const files = sel.size * items.length;

  return (
    <div>
      <p className="mb-4 text-soft">
        Varje bild beskärs runt sin fokuspunkt till exakt rätt mått. Du får en ZIP med en mapp per kanal och en textfil
        med alt-text och fotograf att klistra in när du publicerar. GPS-position och annan kamerainformation tas bort.
      </p>
      <div className="space-y-4">
        {byPlatform.map(([platform, list]) => (
          <div key={platform}>
            <div className="h-section mb-2">{platform}</div>
            <div className="grid grid-cols-3 gap-2">
              {list.map((p) => {
                const on = sel.has(p.id);
                return (
                  <button key={p.id} onClick={() => toggle(p.id)}
                    className={`flex flex-col rounded-lg border p-2 text-left transition ${on ? "border-ember bg-ember-soft/50" : "border-line hover:bg-mist"}`}>
                    <div className="grid h-24 place-items-center rounded bg-mist">
                      {first && (
                        <img src={img(first, 320, `${p.w}:${p.h}`)} alt="" loading="lazy"
                          className="max-h-24 max-w-full rounded-sm object-contain shadow-sm" style={{ aspectRatio: `${p.w}/${p.h}` }} />
                      )}
                    </div>
                    <div className="mt-1.5 flex items-start gap-1.5">
                      <input type="checkbox" readOnly checked={on} className="mt-0.5 accent-ember" />
                      <div className="leading-tight">
                        <div className="text-[13px] font-medium">{p.label}</div>
                        <div className="text-[11.5px] text-muted">{p.w} × {p.h}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {err && <p className="mt-3 text-bad">{err}</p>}
      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-soft">{files} fil{files === 1 ? "" : "er"}</span>
        <button className="btn-primary" disabled={busy || !files} onClick={async () => {
          setBusy(true); setErr("");
          try { await download("export/social", { ids: items.map((i) => i.id), presets: [...sel] }); onDone(); }
          catch (e) { setErr(e instanceof Error ? e.message : "Fel"); }
          finally { setBusy(false); }
        }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Ladda ner ZIP
        </button>
      </div>
    </div>
  );
}

// ---------- PowerPoint ----------
const MODES = [
  { k: "blandat", label: "Bild och text", text: "Mallens layouter med rubrik och bildtext. Layouten väljs efter bildens form." },
  { k: "helbild", label: "Helbild", text: "Fotot fyller hela sliden, med en liten fotobyline." },
  { k: "par", label: "Två per sida", text: "Två bilder bredvid varandra med gemensam rubrik." },
] as const;

function ModeIcon({ k }: { k: string }) {
  const c = "fill-ember/70";
  return (
    <svg viewBox="0 0 64 36" className="h-9 w-16 rounded border border-line bg-card">
      {k === "blandat" && <><rect x="5" y="6" width="22" height="3" rx="1" className="fill-ink/60" /><rect x="5" y="12" width="18" height="2" rx="1" className="fill-ink/25" /><rect x="5" y="16" width="20" height="2" rx="1" className="fill-ink/25" /><rect x="34" y="6" width="25" height="24" rx="1" className={c} /></>}
      {k === "helbild" && <rect x="0" y="0" width="64" height="36" className={c} />}
      {k === "par" && <><rect x="0" y="0" width="24" height="21" className={c} /><rect x="24" y="0" width="24" height="21" className="fill-ember/45" /><rect x="5" y="26" width="24" height="3" rx="1" className="fill-ink/60" /></>}
    </svg>
  );
}

function Deck({ opts, items, onTemplate, onDone }: { opts: Options; items: Thumb[]; onTemplate: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<(typeof MODES)[number]["k"]>("blandat");
  const [cover, setCover] = useState(true);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [captions, setCaptions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const t = opts.template;

  const slides = (cover ? 1 : 0) + (mode === "par" ? Math.ceil(Math.max(0, items.length - (cover ? 1 : 0)) / 2) : Math.max(0, items.length - (cover ? 1 : 0)));

  const uploadTemplate = async (f?: File) => {
    if (!f) return;
    setUploading(true); setErr("");
    const fd = new FormData(); fd.append("file", f);
    try { await api("template", { method: "POST", body: fd }); onTemplate(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Kunde inte läsa mallen"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 rounded-lg bg-mist px-3 py-2.5">
        <Presentation size={18} className="text-ember" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{t ? t.name : "Ingen mall uppladdad"}</div>
          {t && <div className="text-[12px] text-muted">{t.layouts} layouter · omslag ”{t.imageLayouts.cover}”</div>}
        </div>
        <label className="btn cursor-pointer !py-1 text-[13px]">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />} {t ? "Byt mall" : "Ladda upp mall"}
          <input type="file" accept=".potx,.pptx" hidden onChange={(e) => uploadTemplate(e.target.files?.[0])} />
        </label>
      </div>

      <div>
        <div className="label">Upplägg</div>
        <div className="grid grid-cols-3 gap-2">
          {MODES.map((m) => (
            <button key={m.k} onClick={() => setMode(m.k)}
              className={`rounded-lg border p-2.5 text-left transition ${mode === m.k ? "border-ember bg-ember-soft/50" : "border-line hover:bg-mist"}`}>
              <ModeIcon k={m.k} />
              <div className="mt-1.5 text-[13px] font-medium">{m.label}</div>
              <div className="text-[11.5px] leading-snug text-soft">{m.text}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2"><input type="checkbox" checked={cover} onChange={(e) => setCover(e.target.checked)} className="accent-ember" /> Omslag – första bilden blir omslagsbild</label>
        {cover && (
          <div className="grid grid-cols-2 gap-2 pl-6">
            <input className="field" placeholder="Rubrik, t.ex. ”Hjärta för bygden”" value={title} onChange={(e) => setTitle(e.target.value)} />
            <input className="field" placeholder="Underrubrik" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
          </div>
        )}
        <label className="flex items-center gap-2"><input type="checkbox" checked={captions} onChange={(e) => setCaptions(e.target.checked)} className="accent-ember" /> Fotobyline på bilderna</label>
      </div>

      <div>
        <div className="label">Ordning (som du markerade dem)</div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {items.map((i, n) => (
            <div key={i.id} className="relative shrink-0">
              <img src={img(i, 320, "4:3")} alt="" className="h-14 w-[74px] rounded object-cover" />
              <span className="absolute left-1 top-1 rounded bg-ink/70 px-1 text-[10px] font-medium text-white">{cover && n === 0 ? "Omslag" : n + 1 - (cover ? 1 : 0)}</span>
            </div>
          ))}
        </div>
      </div>

      {err && <p className="text-bad">{err}</p>}
      <div className="flex items-center justify-between gap-3">
        <span className="text-soft">{slides} slide{slides === 1 ? "" : "s"}</span>
        <button className="btn-primary" disabled={busy || !t || !items.length} onClick={async () => {
          setBusy(true); setErr("");
          try {
            await download("export/pptx", {
              ids: items.map((i) => i.id), mode, captions,
              cover: cover ? { title: title || "Bilder från Bildbanken", subtitle } : null,
            });
            onDone();
          } catch (e) { setErr(e instanceof Error ? e.message : "Fel"); }
          finally { setBusy(false); }
        }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Skapa PowerPoint
        </button>
      </div>
    </div>
  );
}
